import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AccessDeniedCard from './AccessDeniedCard';

interface AdminRouteProps {
  children: React.ReactNode;
}

// Exported so callers that gate an *action* rather than a route (e.g. the
// Force Quit button in RestaurantAdmin) use the identical normalization. A
// second, hand-rolled role check is how a button and its route guard drift
// apart — feat-274.
export function normalizeRole(raw: string | undefined | null): string | null {
  if (!raw) return null;
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coral mx-auto"></div>
          <p className="mt-4 text-inkMuted">Loading…</p>
        </div>
      </div>
    );
  }

  if (!user || !user.isAuthenticated) {
    return <Navigate to="/scorecard-login" replace />;
  }

  const role = normalizeRole(user.managementUser?.role);
  if (role !== 'Admin') {
    // feat-324: shared card — the denial (Variant B) or, when the role lookup
    // FAILED, "We couldn't load your account" (Variant A). Both offer Sign out.
    return <AccessDeniedCard message="This page is only available to Bespoke administrators." />;
  }

  return <>{children}</>;
};

export default AdminRoute;
